import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useLoading } from "../../contexts/LoadingContext";
import { useTranslation } from "react-i18next";
import SeoMeta from "../../components/SeoMeta";
import COAFields from "./components/COAFields";

function Coa() {
  const { t } = useTranslation();
  const { documentId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const { setIsLoading } = useLoading();

  const STRAPI_URL = process.env.REACT_APP_STRAPI_URL || "http://localhost:1337";
  const STRAPI_TOKEN = process.env.REACT_APP_STRAPI_TOKEN;

  useEffect(() => {
    const fetchData = async () => {
      if (!documentId || !STRAPI_URL || !STRAPI_TOKEN) {
        console.warn("⚠️ Missing config or documentId. Aborting fetch.");
        return;
      }

      const url = `${STRAPI_URL}/api/fine-art-prints/${documentId}?populate=*`;

      setIsLoading(true);
      setError(null);

      try {
        const res = await fetch(url, {
          headers: {
            Authorization: `Bearer ${STRAPI_TOKEN}`,
            "Content-Type": "application/json",
          },
        });

        const result = await res.json();

        if (res.ok && result?.data) {
          setData(result.data);
        } else {
          setError(t("coa.notFound", "Certificate not found."));
        }
      } catch (err) {
        console.error("❌ Fetch error:", err.message);
        setError(t("coa.error", "Network error. Please try again."));
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [documentId, STRAPI_URL, STRAPI_TOKEN, setIsLoading, t]);

  const handleBackToForm = () => {
    navigate("/coa");
  };

  if (error) {
    return (
      <section className="">
        <SeoMeta
          title={t("coa.SeoTitle", "Certificate of Authenticity")}
          description={t("coa.notFound", "Certificate not found.")}
          robots="noindex, nofollow"
        />
			
        <div className="d-flex flex-column container min-vh-100">
          <div className="row align-items-center justify-content-center my-auto">
            <div className="col-md-10">
			  <h1 style={{ lineHeight: '80%' }}>
				<span className="display-2 text-black" style={{ fontWeight: 'bold', lineHeight: '50%' }}>{t("coa.notFoundTitle", "Certificate not found")}</ span>
				<span className="display-4 text-black" style={{ lineHeight: '50%' }}><br />{t("coa.notFoundSubtitle", "Please make sure the 16-character code is entered exactly as shown. It's case-sensitive and should contain no spaces.")}</ span>
			  </h1>			
              <button
                onClick={handleBackToForm}
                className="btn btn-outline-dark btn-rounded mt-4"
              >
                {t("coa.backToForm", "Back to verification form")}
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

		
  if (!data) {
    return (
      <section className="container py-5">
        <h1 className="mb-3">{t("coa.title", "Certificate of Authenticity")}</h1>
        <p className="text-muted">{t("coa.loading", "Loading certificate...")}</p>
      </section>
    );
  }

  return (
    <>
      <SeoMeta
        title={t("coa.SeoTitle", "Certificate of Authenticity")}
        description={`${t("coa.id", "ID")}: ${data.documentId}`}
        robots="noindex, nofollow"
      />

      <section className="container mt-20 mt-xl-20">
        <div className="row mb-5 g-3 g-xl-5 align-items-end">
          <div className="col-lg-12">
            <h1 style={{ lineHeight: "80%" }}>
              <span className="display-4 text-black" style={{ lineHeight: "50%" }}>
                {t("coa.title", "Certificate of Authenticity")}<br />
              </span>
              <span className="display-2 text-black" style={{ fontWeight: "bold", lineHeight: "50%" }}>
                {data.Title}
              </span>
            </h1>
          </div>
        </div>

        <COAFields
          data={data}
          artists={Array.isArray(data.artists) ? data.artists : []}
          printUser={data.printUser || null}
        />
      </section>
    </>
  );
}

export default Coa;
